/**
 * Updates an item in the database with the specified value
 */
export function updateItem(type, key, obj, opts = {}) {
  if (!type || !key) {
    return this.error('Type or key is not defined for update operation')
  }

  type = this.getPlural(type)

  const res = this.sdk.updateItem(type, obj, opts)

  window.requestAnimationFrame(() => {
    if (this.call('hasExecutable', type)) {
      this.testAll(`Updating ${key} in ${type}`, () => [
        this.testCase(
          'Value equals item in root state',
          'isEqualObject',
          { ...obj.value.toString() },
          { ...this.getDataItem(key).toString() }
        ),
        this.testCase(
          'Schema equals item in root state',
          'isEqualObject',
          obj,
          this.getItem(key)
        )
      ])
    } else {
      this.testAll(`Updating ${key} in ${type}`, () => [
        this.testCase(
          'Value equals item in root state',
          'isEqualObject',
          obj.value,
          this.getDataItem(key, type)
        ),
        this.testCase(
          'Schema equals item in root state',
          'isEqualObject',
          obj,
          this.getItem(key, type)
        )
      ])
    }
  })

  return res
}

// USER-CODE-ORIGIN review N5: FAILS CLOSED. The v1 canvas wrote an item into
// the live canvas registry by EVALUATING its stored code
// (evalStringCodeUnsafe → window.eval) on the editor origin — a co-editor's
// expression ran with the session. No live caller remains (the v1
// delegateUpdates chain has none); project code renders only in the opaque
// user-code frame now. A call by name writes nothing.
export async function updateItemInCanvas(type, key) {
  const say = (typeof this.warn === 'function' && this.warn) || null
  if (say) say.call(this, '[updateItemInCanvas] refused: v1 canvas eval path removed (project code renders only in the opaque user-code frame)', type, key)
  return null
}

export async function updateItemInChosenWindow(type, key) {
  if (!this.isCanvas()) {
    this.warn('Updating on canvas requires active canvas page')
    return
  }

  type = this.getPlural(type)

  const data = this.getCanvasScope()
  if (data.previewSize === 'canvas') {
    const { ViewSvitcher } = this.getChosenScene()
    await ViewSvitcher.content?.update()
  } else {
    const ChosenFrame = this.getChosenIframe()
    await ChosenFrame?.update()
  }

  // testing chosen in timeout
  // due to window.onload listener in Chosen iframe
  window.setTimeout(() => {
    this.test(`Updating ${key} ${type} on canvas`, () =>
      this.testCase(
        'Value equals chosen context',
        'isEqualObject',
        this.getDataItem(key, type),
        this.getChosenElementContext()?.[type][key]
      )
    )
  }, 100)
}

export function updateItemInChosenPane(type, key, opts = {}) {
  if (!this.isCanvas()) {
    this.warn('Updating on canvas requires active canvas page')
    return
  }

  type = this.getPlural(type)

  const obj = this.getItem(key, type)
  const { value } = obj
  const ChosenElement = this.getChosenElement()

  ChosenElement.state.set(
    { ...obj, value },
    { skipChosenPane: opts.skipChosenPane, forceMonacoUpdate: true }
  )
}

export function updateItemInSidebar(type, key, opts = {}) {
  if (!this.isCanvas()) {
    this.warn('Updating on canvas requires active canvas page')
    return
  }

  type = this.getPlural(type)

  const item = this.call('findFileSidebarItem', type, key)
  if (!item || opts.preventItemSidebarUpdate) {
    return
  }

  const value = this.getDataItem(key, type)
  item.state.replace(
    { value },
    {
      preventUpdate:
        !item.state.showLayers || opts.preventItemSidebarElementUpdate
    }
  )
}

export async function updateCanvasItem(type, key, opts = {}) {
  if (!type || !key) {
    return this.error('Type or key is not defined for create operation')
  }

  type = this.getPlural(type)

  // updating code editor panel
  const hasChosen = this.getChosenKey()

  if (!opts.skipCanvasUpdate) {
    if (hasChosen) {
      if (hasChosen === key || !opts.skipChosenPane)
        await this.call('updateItemInChosenPane', type, key, opts)
      else await this.call('updateItemInChosenWindow', type, key, opts)
      this.call('queueUpdateItemInCanvas', key)
    } else {
      if (!opts.skipChosenPane)
        await this.call('updateItemInChosenPane', type, key, opts)
      await this.call('updateItemInCanvas', type, key, opts)
    }
  }

  if (!opts.skipSidebar) {
    await this.call('updateItemInSidebar', type, key, opts)
  }

  if (opts.skipRuntimeTests) return

  this.testAll(`Updating ${key} ${type} on canvas`, () => {
    const isExecutable = this.call('hasExecutable', type)
    const isArtboard = this.call('hasArtboard', type)
    const componentOnCanvas = !hasChosen && this.findComponentOnCanvas(key)
    return [
      this.testCase(
        'Item value equals sidebar item value',
        isExecutable ? 'isEqualFunction' : 'isEqualObject',
        this.getDataItem(key, type),
        this.call('findFileSidebarItem', type, key)?.state.value
      ),
      this.testCase(
        'Value equals canvas context',
        isExecutable ? 'isEqualFunction' : 'isEqualObject',
        this.getDataItem(key, type),
        this.getCanvasContext()[type][key]
      ),
      componentOnCanvas &&
        this.testCase(
          'Value equals code editor state',
          'isEqualObject',
          this.getItem(key, type),
          this.getChosenElement().state.parse()
        ),
      componentOnCanvas &&
        this.testCase(
          'Value equals canvas item state',
          'isEqualObject',
          this.getItem(key, type),
          this.findComponentOnCanvas(key).state.parse()
        )
    ]
  })
}
